/**
 * Comms in the Computers section (2026-11-04, decided on public/comms-study.html; the operator: no separate section,
 * "add them to computers section, we can always change it up later"). Three parts, all Navigate kit:
 *   - `CommsNow`: what is wrong right now, as Pre-flight's rows (tap for what to do); "All well" when nothing is.
 *   - `CommsHealth`: under each computer's own row, its round trip, or how long since it was last heard, and what of a
 *     running timeline it plays.
 *   - `CommsEarlier`: every problem, kept across shows (operator: "for future dev work and pattern spotting"), by day.
 * The store is `opbridge/comms.ts`.
 */
import { For, Show, createMemo, createRoot, createSignal, onCleanup } from 'solid-js';
import { commsHistory, commsIssue, commsIssues, spanText, type CommsLevel, type CommsRecord } from '../opbridge/comms.ts';
import { bridges } from '../opbridge/store.ts';
import { transportNow, transportRoundTrips } from '../timeline/transport.ts';
import { getTimeline } from '../timeline/store.ts';
import { timelineParts } from '../timeline/bridgeSync.ts';

const GLYPH: Record<CommsLevel, string> = { bad: '✕', warn: '⚠', info: 'ℹ' };
const STATUS: Record<CommsLevel, string> = { bad: 'fail', warn: 'warn', info: 'info' };

/** A clock that ticks once a second while mounted, so "12 s" climbs. */
function useSecondTick(): () => number {
  const [n, setN] = createSignal(0);
  const t = setInterval(() => setN((x) => x + 1), 1000);
  onCleanup(() => clearInterval(t));
  return n;
}

export function CommsNow() {
  const tick = useSecondTick();
  const [open, setOpen] = createSignal<string | null>(null);
  const live = () => commsIssues();
  const counted = () => live().filter((i) => i.level !== 'info').length;
  return (
    <section class="nv-ss comms-now">
      <div class="nv-ss-h">
        <h2>Right now</h2>
        <Show when={counted() > 0}><span class="nv-ct" data-s={live().some((i) => i.level === 'bad') ? 'warn' : 'live'}>{counted()}</span></Show>
        <p>{live().length > 0 ? 'Tap one for what to do. Each clears itself when it is fixed.' : ''}</p>
      </div>
      <Show when={live().length > 0} fallback={<div class="nv-note" data-tone="info">All well. Every computer answers and every cue lands.</div>}>
        <div class="nav-well">
          <For each={live()}>
            {(i) => (
              <button type="button" class="nav-lr nav-lr-button preflight-row" data-status={STATUS[i.level]} data-issue={i.key}
                aria-expanded={i.todo ? (open() === i.key ? 'true' : 'false') : undefined}
                onClick={() => setOpen(open() === i.key ? null : i.key)}>
                <span class="nv-glyph preflight-row-glyph" data-status={STATUS[i.level]} aria-label={i.level}>{GLYPH[i.level]}</span>
                <span class="nav-lr-main">
                  <b class="preflight-row-label">{i.title}</b>
                  <small class="preflight-row-detail">{i.text}</small>
                  <Show when={i.todo && open() === i.key}><small class="preflight-row-remedy">What to do: {i.todo}</small></Show>
                </span>
                <span class="nav-lr-trail"><small>{(tick(), spanText(Date.now() - i.since))}</small></span>
                <Show when={i.todo}><span class="nav-lr-chev" aria-hidden="true">{open() === i.key ? '⌃' : '›'}</span></Show>
              </button>
            )}
          </For>
        </div>
      </Show>
    </section>
  );
}

/** Which running timelines each computer plays a part of, by bridge id. Worked out when the set of running timelines
 *  changes (a compile per running timeline), never on each reading. */
const playsByBridge = createRoot(() => {
  const playsOn = createMemo(() => {
    const ids = new Set<string>();
    for (const b of bridges()) { const n = transportNow(b.id); if (n && !n.waiting) ids.add(n.timelineId); }
    const key = [...ids].sort().join('|');
    return { key, ids };
  }, undefined, { equals: (a, b) => a.key === b.key });
  return createMemo(() => {
    const out = new Map<string, string[]>();
    for (const id of playsOn().ids) {
      const t = getTimeline(id);
      if (!t) continue;
      try { for (const p of timelineParts(t).parts) out.set(p.cfg.id, [...(out.get(p.cfg.id) ?? []), t.name]); } catch { /* a bad compile says nothing here */ }
    }
    return out;
  });
});

export function CommsHealth(props: { bridgeId: string }) {
  const tick = useSecondTick();
  const lost = () => commsIssue(`lost:${props.bridgeId}`);
  const slow = () => commsIssue(`slow:${props.bridgeId}`);
  const rtt = () => transportRoundTrips()[props.bridgeId];
  const plays = () => playsByBridge().get(props.bridgeId) ?? [];
  const level = () => (lost() ? 'bad' : slow() ? 'warn' : 'ok');
  return (
    <div class="comms-health" data-s={level()}>
      <Show when={lost()} fallback={
        <>
          <Show when={rtt() !== undefined} fallback={<span>Answering</span>}>
            <span>{rtt()! >= 1000 ? `${(rtt()! / 1000).toFixed(1)} s` : `${rtt()} ms`} round trip{slow() ? ' (slow)' : ''}</span>
          </Show>
          <Show when={plays().length > 0}><span class="comms-plays">Plays part of {plays().join(', ')}</span></Show>
        </>
      }>
        {(l) => <span>Can't be seen: last heard {(tick(), spanText(Date.now() - l().since))} ago</span>}
      </Show>
    </div>
  );
}

function dayLabel(at: number): string {
  const d = new Date(at), today = new Date();
  const same = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (same(d, today)) return 'Today';
  const y = new Date(today); y.setDate(today.getDate() - 1);
  if (same(d, y)) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}
const timeOf = (at: number) => new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
const recordText = (r: CommsRecord) => `${r.title}${r.until !== undefined ? `, for ${spanText(r.until - r.at)}` : ''}`;

/** How many records the section shows at once (all are kept: `opbridge/comms.ts` HISTORY_MAX). */
const SHOWN = 60;

export function CommsEarlier() {
  const [all, setAll] = createSignal(false);
  const list = () => (all() ? commsHistory() : commsHistory().slice(0, SHOWN));
  const rows = () => {
    const out: { day?: string; r?: CommsRecord }[] = [];
    let last = '';
    for (const r of list()) {
      const day = dayLabel(r.at);
      if (day !== last) { out.push({ day }); last = day; }
      out.push({ r });
    }
    return out;
  };
  return (
    <section class="nv-ss comms-earlier">
      <div class="nv-ss-h"><h2>Earlier</h2><p>Every problem, kept across shows</p></div>
      <Show when={commsHistory().length > 0} fallback={<div class="nv-note" data-tone="info">Nothing has gone wrong yet.</div>}>
        <div class="nav-well">
          <For each={rows()}>
            {(x) => (
              <Show when={x.r} fallback={<div class="nav-lr comms-day"><span class="nav-lr-main"><b>{x.day}</b></span></div>}>
                {(r) => (
                  <div class="nav-lr comms-record" data-level={r().level}>
                    <span class="comms-time">{timeOf(r().at)}</span>
                    <span class="nv-glyph" data-status={STATUS[r().level]}>{GLYPH[r().level]}</span>
                    <span class="nav-lr-main"><b>{recordText(r())}</b></span>
                  </div>
                )}
              </Show>
            )}
          </For>
        </div>
        <Show when={!all() && commsHistory().length > SHOWN}>
          <div class="nv-row"><button type="button" class="nv-b nv-b-sm" onClick={() => setAll(true)}>Show all {commsHistory().length}</button></div>
        </Show>
      </Show>
    </section>
  );
}
